import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Phone, MapPin, MagnifyingGlass, CaretLeft, CaretRight,
  CheckCircle, XCircle, Clock, ShieldSlash, Trophy, EnvelopeSimple, ArrowsClockwise,
  Eye, EyeSlash,
  GlobeHemisphereWest,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";
import { hapticTap, hapticSuccess, hapticWarn } from "../../lib/haptics";
import { STATUS_BY_ID } from "./callListConstants";
import { useSwipe } from "../../lib/useSwipe";
import CallTimer from "./CallTimer";
import FicheShareLinks from "./FicheShareLinks";
import EmailPreviewModal from "./EmailPreviewModal";
import ObjectionCoachPanel from "./ObjectionCoachPanel";
import QualifyStatusModal, { QUALIFY_STATUSES } from "./QualifyStatusModal";
import SalesScriptModal from "./SalesScriptModal";
import WebsiteLeadModal from "./WebsiteLeadModal";
import CallbackPickModal from "./CallbackPickModal";

/**
 * Mode simple mobile-first — UNE fiche à la fois, boutons énormes, tap-to-call.
 *
 * Pensé pour les téléprospectrices sur téléphone (3/4 des utilisatrices).
 * Zéro texte inutile, un seul choix par écran, boutons min 56px de haut.
 *
 * Props :
 *   - businesses : liste complète (avec .call.status)
 *   - onUpdate(businessId, patch) : mutateur (identique à TeleproCallList)
 *   - onSwitchExpert() : callback pour repasser en mode liste
 */

const NBSP = "\u00A0";

// --- Bouton d'action XL ------------------------------------------------------
function BigActionButton({ colorCls, label, sublabel, icon, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full min-h-[68px] rounded-2xl px-4 py-3 flex items-center gap-3 text-left transition-all active:scale-[0.97] shadow-sm ${colorCls}`}
      data-testid={testid}
    >
      <div className="shrink-0 w-11 h-11 bg-white/25 rounded-full flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-lg leading-tight">{label}</div>
        {sublabel && <div className="text-[11px] opacity-90 leading-tight mt-0.5">{sublabel}</div>}
      </div>
    </button>
  );
}


export default function SimpleCallView({ businesses, onUpdate, onSwitchExpert, telepro, callStartAt, onStartCall, onStopCall }) {
  // Only show entries still "to_call" first, then everything else at the end.
  const ordered = useMemo(() => {
    if (!businesses) return [];
    const todo = businesses.filter((b) => b.call.status === "to_call");
    const done = businesses.filter((b) => b.call.status !== "to_call");
    return [...todo, ...done];
  }, [businesses]);

  const [idx, setIdx] = useState(0);
  const [showAllDone, setShowAllDone] = useState(false);
  const [confirmDnc, setConfirmDnc] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // Vague 13 — Focus mode : cache tout sauf la carte
  const [focusMode, setFocusMode] = useState(() => {
    try { return localStorage.getItem("mc68_telepro_focus_mode") === "1"; }
    catch { return false; }
  });
  const toggleFocus = () => {
    setFocusMode((v) => {
      const n = !v;
      try { localStorage.setItem("mc68_telepro_focus_mode", n ? "1" : "0"); } catch {}
      return n;
    });
  };
  // Vague 13 — Swipe visual feedback
  const [swipeDx, setSwipeDx] = useState(0);
  const cardRef = useRef(null);

  // Auto-jump: when we mark the current call, move to the next todo item.
  useEffect(() => {
    setIdx(0);
    setShowAllDone(false);
  }, [businesses?.length]);

  const total = ordered.length;
  const remaining = ordered.filter((b) => b.call.status === "to_call").length;
  const handled = total - remaining;

  const biz = ordered[idx];
  // fresh list for the delayed auto-advance (ordered is re-sorted after each status change)
  const orderedRef = useRef(ordered);
  orderedRef.current = ordered;
  useEffect(() => { setConfirmDnc(false); }, [biz?.id]);
  const advanceFrom = (doneId) => setTimeout(() => {
    const fresh = orderedRef.current;
    const nextTodo = fresh.findIndex((b) => b.call.status === "to_call" && b.id !== doneId);
    if (nextTodo !== -1) setIdx(nextTodo);
    else setShowAllDone(true);
  }, 400);
  const isLast = idx >= total - 1;
  const isFirst = idx <= 0;

  // ---------------- Handlers ----------------
  const goPrev = () => { hapticTap(); setIdx((i) => Math.max(0, i - 1)); };
  const goNext = () => { hapticTap(); setIdx((i) => Math.min(total - 1, i + 1)); };

  const [qualify, setQualify] = useState(null);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [webLead, setWebLead] = useState(false);
  const [cbPick, setCbPick] = useState(false);

  const applyStatus = async (patch) => {
    if (!biz) return;
    if (patch.status === "sale") hapticSuccess();
    else hapticTap();
    onStopCall?.();
    await onUpdate(biz.id, patch);
    advanceFrom(biz.id);
  };
  // VAGUE 42 — refus / intéressé : choix du motif ou de l'offre d'abord
  const markStatus = (statusId) => {
    if (QUALIFY_STATUSES.has(statusId)) return setQualify(statusId);
    if (statusId === "site_web_besoin") return setWebLead(true);
    if (statusId === "callback") return setCbPick(true);
    return applyStatus({ status: statusId });
  };

  // Vague 13 — Swipe gauche/droite pour marquer statut
  const swipeHandlers = useSwipe({
    onSwipeLeft: () => {
      if (biz && biz.call.status === "to_call") {
        toast.info("← Refus", { duration: 1200 });
        markStatus("refused");
      }
    },
    onSwipeRight: () => {
      if (biz && biz.call.status === "to_call") {
        toast.info("Rappeler →", { duration: 1200 });
        markStatus("callback");
      }
    },
    onSwipeProgress: (dx) => setSwipeDx(dx),
    threshold: 90,
  });

  const markDnc = async () => {
    if (!biz) return;
    hapticWarn();
    const id = biz.id;
    setConfirmDnc(false);  // close BEFORE the list re-sorts, so it never shows on the next prospect
    await onUpdate(id, { status: "refused_final", refusal_reason: "no_more_contact" });
    advanceFrom(id);
  };

  // ---------------- Empty / all-done ----------------
  if (!biz) {
    return (
      <div className="bg-primary/5 border border-primary/30 rounded-2xl p-8 text-center" data-testid="simple-empty-state">
        <Trophy size={40} weight="duotone" className="text-primary mx-auto mb-3"/>
        <div className="font-display text-xl font-medium">Rien à appeler aujourd&apos;hui !</div>
      </div>
    );
  }

  if (showAllDone && remaining === 0) {
    return (
      <div className="bg-primary/10 border-2 border-primary rounded-2xl p-8 text-center" data-testid="simple-all-done">
        <div className="text-6xl mb-2">🎉</div>
        <div className="font-display text-2xl font-bold text-primary mb-1">Bravo !</div>
        <div className="text-sm text-foreground/80 mb-5">
          Tu as traité les {total} entreprises de ta liste du jour.
        </div>
        <button
          type="button"
          onClick={() => { setShowAllDone(false); setIdx(0); }}
          className="bg-card border border-border hover:border-primary hover:text-primary text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2 mb-3"
          data-testid="simple-review-btn"
        >
          <ArrowsClockwise size={12} weight="bold"/> Revoir mes fiches
        </button>
      </div>
    );
  }

  const call = biz.call || {};
  const status = STATUS_BY_ID[call.status] || {};
  const isDone = call.status !== "to_call";
  const phone = call.contact_phone || biz.phone || "";
  const email = call.contact_email || biz.email || "";
  const website = biz.website || "";
  const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(`${biz.name} ${biz.city || ""} contact`)}`;

  return (
    <div
      ref={cardRef}
      className={`bg-card border border-border rounded-2xl overflow-hidden shadow-md relative select-none ${focusMode ? "sm:max-w-2xl sm:mx-auto" : ""}`}
      style={{
        transform: swipeDx ? `translateX(${Math.max(-140, Math.min(140, swipeDx))}px) rotate(${swipeDx / 40}deg)` : undefined,
        transition: swipeDx ? "none" : "transform 0.25s ease-out",
      }}
      data-testid="simple-call-view"
      {...swipeHandlers}
    >
      {/* Swipe hint indicators */}
      {Math.abs(swipeDx) > 20 && biz.call.status === "to_call" && (
        <>
          {swipeDx < 0 && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 z-10 pointer-events-none">
              <div className="bg-secondary text-white rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wider shadow-lg" data-testid="swipe-hint-refused">
                ← Refus
              </div>
            </div>
          )}
          {swipeDx > 0 && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 z-10 pointer-events-none">
              <div className="bg-[#C8795A] text-white rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wider shadow-lg" data-testid="swipe-hint-callback">
                Rappeler →
              </div>
            </div>
          )}
        </>
      )}

      {/* Header : compteur + mode expert + focus toggle */}
      <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between gap-2">
        <div className="text-xs opacity-80 uppercase tracking-wider font-bold flex items-center gap-2">
          Fiche {idx + 1} sur {total}
          {callStartAt && <CallTimer startAt={callStartAt} onStop={onStopCall} compact/>}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleFocus}
            className="text-[10px] uppercase tracking-wider font-bold bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-sm inline-flex items-center gap-1"
            data-testid="focus-mode-toggle"
            title={focusMode ? "Sortir du mode focus" : "Focus : cacher tout sauf la fiche"}
            aria-pressed={focusMode}
          >
            {focusMode ? <EyeSlash size={11} weight="bold"/> : <Eye size={11} weight="bold"/>}
            <span className="hidden xs:inline">Focus</span>
          </button>
          <button
            type="button"
            onClick={onSwitchExpert}
            className="text-[10px] uppercase tracking-wider font-bold bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-sm"
            data-testid="switch-to-expert-btn"
            title="Voir la liste complète (mode expert)"
          >
            📋 Liste
          </button>
        </div>
      </div>

      {/* Progress bar géante */}
      <div className="h-3 bg-muted">
        <div
          className="h-full bg-gradient-to-r from-primary/70 to-primary transition-all duration-500"
          style={{ width: `${total ? Math.round((handled / total) * 100) : 0}%` }}
          data-testid="simple-progress-bar"
        />
      </div>

      {/* ID de statut si résolue */}
      {isDone && (
        <div className={`px-4 py-2 text-center text-xs font-bold uppercase tracking-wider ${status.color || "bg-muted"}`} data-testid="simple-status-tag">
          {status.label || "Traitée"} · déjà noté
        </div>
      )}

      {/* Nom de l'entreprise */}
      <div className="px-5 pt-6 pb-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">
          Entreprise à appeler
        </div>
        <h2 className="font-display font-semibold text-2xl sm:text-3xl leading-tight text-foreground break-words" data-testid="simple-business-name">
          {biz.name}
        </h2>
        <div className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
          <MapPin size={14} weight="duotone" className="mt-0.5 shrink-0 text-primary"/>
          <div>
            {biz.address}<br/>
            <span className="font-medium text-foreground">{biz.postal_code} {biz.city}</span>
          </div>
        </div>
        {biz.category && (
          <div className="mt-2 inline-block bg-muted text-foreground/70 text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm">
            {biz.category}
          </div>
        )}
      </div>

      {/* Téléphone GÉANT tap-to-call */}
      <div className="px-5 pb-4 space-y-2.5">
        {phone ? (
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            onClick={() => { hapticTap(); onStartCall?.(); }}
            className="w-full bg-primary text-primary-foreground rounded-2xl px-4 py-4 flex items-center gap-3 shadow-sm hover:bg-[#0f3d29] transition-colors active:scale-[0.98]"
            data-testid="simple-call-phone"
          >
            <div className="shrink-0 w-12 h-12 bg-white/25 rounded-full flex items-center justify-center">
              <Phone size={22} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Appeler maintenant</div>
              <div className="font-mono text-xl font-bold tracking-wide">{phone}</div>
            </div>
          </a>
        ) : (
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-secondary text-white rounded-2xl px-4 py-4 flex items-center gap-3 shadow-sm hover:bg-[#a5432c] transition-colors active:scale-[0.98]"
            data-testid="simple-google-search"
          >
            <div className="shrink-0 w-12 h-12 bg-white/25 rounded-full flex items-center justify-center">
              <MagnifyingGlass size={22} weight="bold"/>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[10px] uppercase tracking-wider opacity-90 font-bold">Pas de téléphone</div>
              <div className="font-display text-base font-bold">Chercher sur Google</div>
              <div className="text-[10px] opacity-90 leading-tight mt-0.5">
                Ouvre {biz.name} + {biz.city || "ville"} + « contact »
              </div>
            </div>
          </a>
        )}

        {(email || website) && (
          <div className="flex gap-2">
            {email && (
              <button
                type="button"
                onClick={() => setEmailOpen(true)}
                className="flex-1 bg-card border border-border hover:border-primary hover:text-primary rounded-xl px-3 py-2.5 flex items-center justify-center gap-1.5 text-xs font-semibold truncate"
                data-testid="simple-email"
              >
                <EnvelopeSimple size={14} weight="bold"/> Email
              </button>
            )}
            {website && (
              <a
                href={website.startsWith("http") ? website : `https://${website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-card border border-border hover:border-primary hover:text-primary rounded-xl px-3 py-2.5 flex items-center justify-center gap-1.5 text-xs font-semibold"
                data-testid="simple-website"
              >
                🌐 Site web
              </a>
            )}
            {phone && (
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-card border border-border hover:border-primary hover:text-primary rounded-xl px-3 py-2.5 flex items-center justify-center gap-1 text-xs font-semibold"
                data-testid="simple-google-btn"
                title="Chercher sur Google"
              >
                <MagnifyingGlass size={12} weight="bold"/>
              </a>
            )}
          </div>
        )}
        <FicheShareLinks biz={biz} phone={phone} teleproCode={telepro?.code}/>
      </div>

      <button
        type="button"
        onClick={() => setScriptOpen(true)}
        className="w-full text-xs font-bold text-primary bg-card border border-primary/40 rounded-xl px-3 py-2.5"
        data-testid="simple-sales-script-btn"
      >
        Argumentaire IA pour ce prospect
      </button>
      {scriptOpen && <SalesScriptModal biz={biz} onClose={() => setScriptOpen(false)}/>}
      {cbPick && (
        <CallbackPickModal biz={biz} onClose={() => setCbPick(false)} onPick={(patch) => {
          setCbPick(false);
          applyStatus(Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined)));
        }}/>
      )}
      {webLead && (
        <WebsiteLeadModal biz={biz} onClose={() => setWebLead(false)} onSubmit={(patch) => { setWebLead(false); applyStatus(patch); }}/>
      )}

      <details className="group" data-testid="simple-coach-toggle">
        <summary className="cursor-pointer list-none text-xs font-bold text-primary bg-primary/5 border border-primary/25 rounded-xl px-3 py-2.5 text-center">
          Coach : que répondre au prospect ?
        </summary>
        <div className="mt-2">
          <ObjectionCoachPanel biz={biz} onQualify={applyStatus} onStatus={markStatus}/>
        </div>
      </details>

      {qualify && (
        <QualifyStatusModal
          status={qualify}
          bizName={biz.name}
          bizId={biz.id}
          onClose={() => setQualify(null)}
          onPick={(patch) => { setQualify(null); applyStatus(patch); }}
        />
      )}

      {emailOpen && (
        <EmailPreviewModal
          biz={biz}
          telepro={telepro}
          contactName={call.contact_name}
          contactEmail={email}
          onClose={() => setEmailOpen(false)}
          onSent={() => setEmailOpen(false)}
        />
      )}

      {/* --- 4 GROS BOUTONS D'ACTION --- */}
      <div className="px-5 pb-5 space-y-2.5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">
          Résultat de l&apos;appel
        </div>
        <BigActionButton
          testid="simple-btn-sale"
          onClick={() => markStatus("sale")}
          colorCls="bg-primary text-primary-foreground hover:bg-[#0f3d29]"
          icon={<CheckCircle size={22} weight="fill"/>}
          label="Vente !"
          sublabel="Le commerçant s'inscrit — bravo !"
        />
        <BigActionButton
          testid="simple-btn-callback"
          onClick={() => markStatus("callback")}
          colorCls="bg-[#C8795A] text-white hover:bg-[#a5432c]"
          icon={<Clock size={22} weight="duotone"/>}
          label="Rappeler plus tard"
          sublabel="Il a demandé qu'on le recontacte à un autre moment"
        />
        <BigActionButton
          testid="simple-btn-no-answer"
          onClick={() => markStatus("no_answer")}
          colorCls="bg-muted-foreground text-white hover:bg-foreground"
          icon={<Phone size={22} weight="bold"/>}
          label="Pas joint"
          sublabel="Personne n'a décroché, répondeur, occupé…"
        />
        <BigActionButton
          testid="simple-btn-refused"
          onClick={() => markStatus("refused")}
          colorCls="bg-secondary text-white hover:bg-[#a5432c]"
          icon={<XCircle size={22} weight="bold"/>}
          label="Refus"
          sublabel="« Pas intéressé aujourd'hui » — mais poli"
        />
        <BigActionButton
          testid="simple-btn-site-web"
          onClick={() => markStatus("site_web_besoin")}
          colorCls="bg-[#1a5f8a] text-white hover:bg-[#154a6c]"
          icon={<GlobeHemisphereWest size={22} weight="bold"/>}
          label="Besoin site web"
          sublabel="Création, refonte, référencement… → Digital Corp France"
        />

        {/* Ne plus contacter — moins visible, avec confirm */}
        {!confirmDnc ? (
          <button
            type="button"
            onClick={() => setConfirmDnc(true)}
            className="w-full text-xs uppercase tracking-wider font-bold text-secondary/80 hover:text-secondary py-2 inline-flex items-center justify-center gap-1.5"
            data-testid="simple-btn-open-dnc"
          >
            <ShieldSlash size={13} weight="bold"/>
            Il dit « ne me rappelez plus jamais »
          </button>
        ) : (
          <div className="border-2 border-secondary rounded-xl p-3 bg-secondary/5" data-testid="simple-dnc-confirm">
            <div className="text-xs font-bold text-secondary mb-2 uppercase tracking-wider">
              ⚠️ Ne plus contacter — définitif
            </div>
            <p className="text-xs mb-3 leading-relaxed">
              Cette entreprise <b>disparaîtra de toutes les listes d&apos;appels</b> (aussi pour les autres télépros)
              et sera ajoutée au registre RGPD. À faire seulement si le commerçant l&apos;a clairement demandé.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={markDnc}
                className="flex-1 bg-secondary text-white hover:bg-[#a5432c] px-3 py-2.5 rounded-lg text-sm font-bold"
                data-testid="simple-btn-confirm-dnc"
              >
                Oui, ne plus contacter
              </button>
              <button
                type="button"
                onClick={() => setConfirmDnc(false)}
                className="bg-card border border-border hover:bg-muted px-3 py-2.5 rounded-lg text-xs font-semibold"
                data-testid="simple-btn-cancel-dnc"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notes rapides */}
      {call.note && (
        <div className="mx-5 mb-4 bg-muted rounded-lg p-3 text-xs" data-testid="simple-note-preview">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Ma note</div>
          <div className="whitespace-pre-line">{call.note}</div>
        </div>
      )}

      {/* Nav Précédent / Suivant */}
      <div className="border-t border-border bg-muted/30 px-3 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={goPrev}
          disabled={isFirst}
          className="flex items-center gap-1 px-3 py-2.5 rounded-lg text-xs font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-card min-h-[44px]"
          data-testid="simple-btn-prev"
        >
          <CaretLeft size={14} weight="bold"/> Précédent
        </button>
        <div className="flex-1 text-center text-[11px] text-muted-foreground">
          <span className="font-bold text-foreground tabular-nums">{handled}</span>{NBSP}/{NBSP}{total} traités
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70 mt-0.5 hidden sm:block">
            💡 Sur mobile : glisse ← Refus · Rappeler →
          </div>
        </div>
        <button
          type="button"
          onClick={goNext}
          disabled={isLast}
          className="flex items-center gap-1 px-3 py-2.5 rounded-lg text-xs font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-card min-h-[44px]"
          data-testid="simple-btn-next"
        >
          Suivant <CaretRight size={14} weight="bold"/>
        </button>
      </div>
    </div>
  );
}
